import { useState, useMemo, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, RotateCcw, Loader2, ChevronDown, ChevronRight } from 'lucide-react'
import { EntityPageHeader, SearchInput, TablePagination } from '@/shared/components'
import { useErrorLogs } from '../hooks/use-error-logs'
import type { ErrorLogFilters } from '../types/error-log'
import { cn } from '@/lib/utils'

const HTTP_STATUSES = [400, 403, 404, 409, 500] as const

const PAGE_SIZE = 20

const STATUS_STYLES: Record<number, string> = {
  400: 'bg-yellow-50 text-yellow-700',
  403: 'bg-orange-50 text-orange-700',
  404: 'bg-neutral-100 text-neutral-600',
  409: 'bg-amber-50 text-amber-700',
  500: 'bg-red-50 text-red-700',
}

function formatDateTime(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getStatusLabel(status: number): string {
  switch (status) {
    case 400: return '400 Bad Request'
    case 403: return '403 Forbidden'
    case 404: return '404 Not Found'
    case 409: return '409 Conflict'
    case 500: return '500 Server Error'
    default: return String(status)
  }
}

export function AdminErrorsPage() {
  const { t } = useTranslation()

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<number | ''>('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(0)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const filters: ErrorLogFilters = useMemo(() => ({
    page,
    size: PAGE_SIZE,
    ...(search && { search }),
    ...(statusFilter && { httpStatus: statusFilter }),
    ...(dateFrom && { from: dateFrom + 'T00:00:00' }),
    ...(dateTo && { to: dateTo + 'T23:59:59' }),
  }), [page, search, statusFilter, dateFrom, dateTo])

  const { data, isLoading } = useErrorLogs(filters)

  const handleReset = () => {
    setSearch('')
    setStatusFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(0)
    setExpandedId(null)
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(0)
  }

  const hasFilters = search || statusFilter || dateFrom || dateTo

  return (
    <div className="space-y-6">
      <EntityPageHeader
        icon={AlertTriangle}
        title={t('admin.errors.title', "Journal d'erreurs")}
        subtitle={t('admin.errors.subtitle', 'Historique des erreurs applicatives')}
      />

      {/* Filters */}
      <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder={t('admin.errors.searchPlaceholder', 'Rechercher par type, message, URL...')}
            className="w-64"
          />

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500">
              {t('admin.errors.filterStatus', 'Statut HTTP')}
            </label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value ? Number(e.target.value) : ''); setPage(0) }}
              className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 transition-colors hover:border-neutral-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            >
              <option value="">{t('admin.errors.allStatuses', 'Tous')}</option>
              {HTTP_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {getStatusLabel(s)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500">
              {t('admin.errors.dateFrom', 'Du')}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0) }}
              className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 transition-colors hover:border-neutral-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500">
              {t('admin.errors.dateTo', 'Au')}
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0) }}
              className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 transition-colors hover:border-neutral-300 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
          </div>

          {hasFilters && (
            <button
              onClick={handleReset}
              className="flex h-10 items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t('admin.errors.reset', 'Réinitialiser')}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !data || data.content.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
              <AlertTriangle className="h-6 w-6 text-emerald-400" />
            </div>
            <p className="mt-3 text-sm font-medium text-neutral-900">
              {t('admin.errors.empty', 'Aucune erreur trouvée')}
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              {hasFilters
                ? t('admin.errors.emptyFiltered', 'Essayez de modifier vos filtres.')
                : t('admin.errors.emptyAll', "Aucune erreur n'a été enregistrée.")}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50/50">
                    <th className="w-8 px-2 py-3" />
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.errors.col.date', 'Date')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.errors.col.status', 'Statut')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.errors.col.type', 'Type')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.errors.col.message', 'Message')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.errors.col.endpoint', 'Endpoint')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                      {t('admin.errors.col.user', 'Utilisateur')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {data.content.map((error) => (
                    <Fragment key={error.id}>
                      <tr
                        key={error.id}
                        className={cn(
                          'cursor-pointer transition-colors hover:bg-neutral-50/50',
                          expandedId === error.id && 'bg-neutral-50/50',
                        )}
                        onClick={() => setExpandedId(expandedId === error.id ? null : error.id)}
                      >
                        <td className="px-2 py-3 text-neutral-400">
                          {error.stackTrace ? (
                            expandedId === error.id
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />
                          ) : null}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-neutral-600">
                          {formatDateTime(error.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn(
                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                            STATUS_STYLES[error.httpStatus] || 'bg-neutral-100 text-neutral-600',
                          )}>
                            {error.httpStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-neutral-700">
                          {error.errorType}
                        </td>
                        <td className="max-w-xs truncate px-4 py-3 text-neutral-600" title={error.message || ''}>
                          {error.message || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {error.requestMethod && (
                            <span className="mr-1.5 inline-flex items-center rounded bg-neutral-100 px-1.5 py-0.5 text-xs font-medium text-neutral-600">
                              {error.requestMethod}
                            </span>
                          )}
                          <span className="font-mono text-xs text-neutral-500">
                            {error.requestUrl || '—'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-neutral-600">
                          {error.userEmail || '—'}
                        </td>
                      </tr>
                      {expandedId === error.id && error.stackTrace && (
                        <tr>
                          <td colSpan={7} className="bg-neutral-900 px-6 py-4">
                            <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-xs leading-relaxed text-neutral-300">
                              {error.stackTrace}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <TablePagination
              currentPage={data.number + 1}
              totalPages={data.totalPages}
              totalItems={data.totalElements}
              pageSize={data.size}
              onPageChange={(p) => setPage(p - 1)}
            />
          </>
        )}
      </div>
    </div>
  )
}
